import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  fetchDevices,
  fetchDeviceById,
  fetchDeviceGroups,
  fetchReadoutPlans,
  fetchAppliances,
  saveDevice,
  resolvePendingInvitations,
} from '../services/deviceService';
import { Device } from '../../types';

// Query keys
export const deviceKeys = {
  all: ['devices'] as const,
  lists: () => [...deviceKeys.all, 'list'] as const,
  list: (filters: any) => [...deviceKeys.lists(), { filters }] as const,
  details: () => [...deviceKeys.all, 'detail'] as const,
  detail: (id: string) => [...deviceKeys.details(), id] as const,
  groups: () => [...deviceKeys.all, 'groups'] as const,
  readoutPlans: (deviceId: string) => [...deviceKeys.detail(deviceId), 'readoutPlans'] as const,
  appliances: (deviceId: string) => [...deviceKeys.detail(deviceId), 'appliances'] as const,
};

/**
 * Hook to fetch all devices
 */
export const useDevices = () => {
  return useQuery(deviceKeys.lists(), fetchDevices, {
    staleTime: 5 * 60 * 1000, // 5 minutes,
    // Don't automatically fetch on mount, wait for explicit refetch
    enabled: false,
  });
};

/**
 * Hook to fetch a single device by ID
 */
export const useDevice = (id?: string) => {
  return useQuery(deviceKeys.detail(id || ''), () => fetchDeviceById(id || ''), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch device groups
 */
export const useDeviceGroups = () => {
  return useQuery(deviceKeys.groups(), fetchDeviceGroups, {
    staleTime: 10 * 60 * 1000, // 10 minutes,
    // Don't automatically fetch on mount, wait for explicit refetch
    enabled: false,
  });
};

/**
 * Hook to fetch readout plans for a device
 */
export const useReadoutPlans = (deviceId?: string) => {
  return useQuery(
    deviceKeys.readoutPlans(deviceId || ''),
    () => fetchReadoutPlans(deviceId || ''),
    {
      enabled: !!deviceId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

/**
 * Hook to fetch appliances for a device
 */
export const useAppliances = (deviceId?: string) => {
  return useQuery(deviceKeys.appliances(deviceId || ''), () => fetchAppliances(deviceId || ''), {
    enabled: !!deviceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to save (create or update) a device
 */
export const useSaveDevice = () => {
  const queryClient = useQueryClient();

  return useMutation(saveDevice, {
    // When mutate is called:
    onMutate: async (newDevice) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(deviceKeys.lists());

      // Snapshot the previous value
      const previousDevices = queryClient.getQueryData(deviceKeys.lists());

      // Optimistically update to the new value
      if (newDevice.id) {
        queryClient.setQueryData(deviceKeys.detail(newDevice.id), newDevice);

        // Update the device in the list
        queryClient.setQueryData(deviceKeys.lists(), (old: any) => {
          if (!old) return old;
          return old.map((device: Device) => (device.id === newDevice.id ? newDevice : device));
        });
      }

      return { previousDevices };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (_err, newDevice, context: any) => {
      if (context?.previousDevices) {
        queryClient.setQueryData(deviceKeys.lists(), context.previousDevices);
      }
      if (newDevice.id) {
        queryClient.invalidateQueries(deviceKeys.detail(newDevice.id));
      }
    },
    // Always refetch after error or success:
    onSettled: (data) => {
      queryClient.invalidateQueries(deviceKeys.lists());
      if (data?.id) {
        queryClient.invalidateQueries(deviceKeys.detail(data.id));
      }
    },
  });
};

/**
 * Hook to resolve pending invitations
 */
export const useResolvePendingInvitations = () => {
  const queryClient = useQueryClient();

  return useMutation(resolvePendingInvitations, {
    onSuccess: () => {
      // Invalidate and refetch devices list
      queryClient.invalidateQueries(deviceKeys.lists());
    },
  });
};
